import * as XLSX from "xlsx";

// Lectura y diagnostico del Excel del pedido de un cliente. Reconoce dos formas
// de tabla, cada una con su propia fila de encabezado (la que trae clave/nombre):
//  - SEMANAL: columnas LUNES...DOMINGO (+ opcional MENSUAL)
//  - CALENDARIO: una columna por fecha real del mes
// Un mismo renglon puede traer cantidades en ambos, no son exclusivos.

const MXN = "60c7e4396465ad440c41c15a";

const DIAS_SEMANA = [
  { dia: 1, nombre: "LUNES", abreviaciones: ["LUN", "LU", "L"] },
  { dia: 2, nombre: "MARTES", abreviaciones: ["MAR", "MA"] },
  { dia: 3, nombre: "MIERCOLES", abreviaciones: ["MIE", "MI", "X"] },
  { dia: 4, nombre: "JUEVES", abreviaciones: ["JUE", "JU", "J"] },
  { dia: 5, nombre: "VIERNES", abreviaciones: ["VIE", "VI", "V"] },
  { dia: 6, nombre: "SABADO", abreviaciones: ["SAB", "SA", "S"] },
  { dia: 7, nombre: "DOMINGO", abreviaciones: ["DOM", "DO", "D"] },
];

// Rango de marcas diacriticas combinadas (U+0300 a U+036F) que deja NFD.
const ACENTOS = new RegExp("[\\u0300-\\u036f]", "g");

const normalizar = (valor) => {
  if (valor === undefined || valor === null) return "";
  return String(valor).normalize("NFD").replace(ACENTOS, "").trim().toUpperCase();
};

const esCeldaVacia = (valor) => valor === undefined || valor === null || String(valor).trim() === "";

// cellDates: true para que las fechas lleguen como Date y no como serial de Excel
export function leerArchivoExcel(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new Error("No se pudo leer el archivo"));
    reader.onloadend = (e) => {
      try {
        const data = new Uint8Array(e.target.result);
        resolve(XLSX.read(data, { type: "array", cellDates: true }));
      } catch (error) {
        reject(error);
      }
    };
    reader.readAsArrayBuffer(file);
  });
}

function detectarDiasSemana(grid) {
  const encontrados = [];
  grid.forEach((fila, indiceFila) => {
    if (!Array.isArray(fila)) return;
    fila.forEach((celda, indiceColumna) => {
      const texto = normalizar(celda);
      if (!texto || texto.length > 30) return;
      const dia = DIAS_SEMANA.find(
        (value) => texto.includes(value.nombre) || value.abreviaciones.includes(texto)
      );
      if (dia) {
        encontrados.push({
          fila: indiceFila + 1,
          columna: indiceColumna + 1,
          letra: XLSX.utils.encode_col(indiceColumna),
          valor: celda,
          dia: dia.dia,
          nombreDia: dia.nombre,
        });
      }
    });
  });
  return encontrados;
}

// Celdas que Excel guarda como fecha real (pista del modo CALENDARIO).
function detectarFechas(grid) {
  const encontradas = [];
  grid.forEach((fila, indiceFila) => {
    if (!Array.isArray(fila)) return;
    fila.forEach((celda, indiceColumna) => {
      if (celda instanceof Date) {
        encontradas.push({
          fila: indiceFila + 1,
          columna: indiceColumna + 1,
          letra: XLSX.utils.encode_col(indiceColumna),
          valor: celda,
        });
      }
    });
  });
  return encontradas;
}

function detectarFilaEncabezado(grid) {
  const limite = Math.min(grid.length, 20);
  let mejor = null;
  for (let i = 0; i < limite; i += 1) {
    const fila = Array.isArray(grid[i]) ? grid[i] : [];
    const textos = fila.filter((celda) => !esCeldaVacia(celda) && typeof celda === "string");
    if (textos.length >= 3 && (!mejor || textos.length > mejor.celdasTexto)) {
      mejor = { fila: i + 1, celdasTexto: textos.length, valores: fila };
    }
  }
  return mejor;
}

export function analizarLibro(workbook, nombreArchivo) {
  const hojas = workbook.SheetNames.map((sheetName) => {
    const hoja = workbook.Sheets[sheetName];
    const ref = hoja["!ref"] || "";
    const rango = ref ? XLSX.utils.decode_range(ref) : null;

    const rangoDesdeA1 = rango ? { s: { r: 0, c: 0 }, e: { r: rango.e.r, c: rango.e.c } } : undefined;
    const opcionesRejilla = { header: 1, defval: "", range: rangoDesdeA1 };

    const grid = XLSX.utils.sheet_to_json(hoja, opcionesRejilla);
    const gridFormateado = XLSX.utils.sheet_to_json(hoja, { ...opcionesRejilla, raw: false });

    const columnasDelRango = rango ? rango.e.c + 1 : 0;
    const columnasDeLaRejilla = grid.reduce(
      (maximo, fila) => (Array.isArray(fila) && fila.length > maximo ? fila.length : maximo),
      0
    );
    const totalColumnas = Math.max(columnasDelRango, columnasDeLaRejilla);

    return {
      nombre: sheetName,
      ref,
      totalFilas: grid.length,
      totalColumnas,
      filasNoVacias: grid.filter((fila) => Array.isArray(fila) && fila.some((celda) => !esCeldaVacia(celda))).length,
      columnas: Array.from({ length: totalColumnas }, (_, i) => XLSX.utils.encode_col(i)),
      merges: (hoja["!merges"] || []).map((merge) => ({
        ref: XLSX.utils.encode_range(merge),
        inicio: { fila: merge.s.r + 1, columna: XLSX.utils.encode_col(merge.s.c) },
        fin: { fila: merge.e.r + 1, columna: XLSX.utils.encode_col(merge.e.c) },
      })),
      filaEncabezadoProbable: detectarFilaEncabezado(grid),
      pistas: {
        diasSemana: detectarDiasSemana(grid),
        fechas: detectarFechas(grid),
      },
      grid,
      gridFormateado,
    };
  });

  return {
    nombreArchivo,
    totalHojas: hojas.length,
    nombresHojas: workbook.SheetNames,
    hojas,
  };
}

const SINONIMOS_COLUMNA = {
  lote: ["LOTE", "GRUPO", "CATEGORIA"],
  clave: ["C.B.A", "C.B.A.", "CBA", "SKU"],
  nombre: ["NOMBRE DEL ALIMENTO", "NOMBRE DEL PRODUCTO", "DESCRIPCION/GRAMAJE", "DESCRIPCION", "CONCEPTO"],
  presentacion: ["PRESENTACION", "UNIDAD DE MEDIDA", "UDM"],
  mensual: ["MENSUAL"],
};

const localizarColumnaExacta = (filaEncabezado, sinonimos, columnasOcupadas) => {
  const normalizada = filaEncabezado.map((celda) => normalizar(celda));
  for (const texto of sinonimos) {
    const indice = normalizada.findIndex((valor, i) => valor === texto && !columnasOcupadas.has(i));
    if (indice !== -1) return indice;
  }
  return -1;
};

const parseNumero = (valor) => {
  if (valor === undefined || valor === null || valor === "") return 0;
  if (typeof valor === "number") return Number.isFinite(valor) ? valor : 0;
  const limpio = String(valor).replace(/[^0-9.-]/g, "");
  const numero = parseFloat(limpio);
  return Number.isNaN(numero) ? 0 : numero;
};

function agruparCandidatas(pistas, minimo, identidad) {
  const porFila = new Map();
  pistas.forEach((celda) => {
    const lista = porFila.get(celda.fila) || [];
    if (!identidad || !lista.some((item) => identidad(item) === identidad(celda))) lista.push(celda);
    porFila.set(celda.fila, lista);
  });
  return [...porFila.entries()]
    .filter(([, celdas]) => celdas.length >= minimo)
    .sort((a, b) => b[1].length - a[1].length)
    .map(([fila, celdas]) => ({ fila, celdas }));
}

function localizarFilaValida(candidatas, hoja) {
  for (const candidata of candidatas) {
    const filaEncabezado = hoja.grid[candidata.fila - 1] || [];
    const ocupadas = new Set(candidata.celdas.map((item) => item.columna - 1));
    const clave = localizarColumnaExacta(filaEncabezado, SINONIMOS_COLUMNA.clave, ocupadas);
    const nombre = localizarColumnaExacta(filaEncabezado, SINONIMOS_COLUMNA.nombre, ocupadas);
    if (clave !== -1 && nombre !== -1) return candidata;
  }
  return null;
}

function candidatasDeDias(hoja) {
  return agruparCandidatas(hoja.pistas.diasSemana, 5, (celda) => celda.dia);
}

function candidatasDeFechas(hoja) {
  return agruparCandidatas(hoja.pistas.fechas, 5);
}

// Variante de CALENDARIO con columnas pareadas INICIAL/FINAL por dia, donde el
// encabezado real de columnas (SKU/CONCEPTO/UDM/INICIAL/FINAL...) esta en una
// fila vecina a la fila de fechas. Dos cosas varian segun quien arme el
// archivo: el orden de las filas (a veces las fechas van arriba y el
// encabezado abajo, a veces al reves) y en cual de las dos columnas del par
// queda la fecha (a veces en la FINAL, a veces en la INICIAL de al lado) - se
// prueban ambas direcciones de fila, y para cada fecha se ubica la columna
// FINAL real revisando su propio indice y el siguiente.
// No se toca localizarFilaValida para no arriesgar SEMANAL/CALENDARIO clasico.
const DESPLAZAMIENTOS_ENCABEZADO_CALENDARIO_FINAL = [1, -1, 2, -2];

function localizarBloqueCalendarioFinal(candidatas, hoja) {
  for (const candidata of candidatas) {
    const indiceFilaFechas = candidata.fila - 1;

    for (const desplazamiento of DESPLAZAMIENTOS_ENCABEZADO_CALENDARIO_FINAL) {
      const indiceFilaEncabezado = indiceFilaFechas + desplazamiento;
      const filaEncabezado = hoja.grid[indiceFilaEncabezado];
      if (!Array.isArray(filaEncabezado) || !filaEncabezado.length) continue;

      const celdasFinal = [];
      candidata.celdas.forEach((celda) => {
        const col = celda.columna - 1;
        if (normalizar(filaEncabezado[col]) === "FINAL") {
          celdasFinal.push({ valor: celda.valor, columna: col + 1 });
        } else if (normalizar(filaEncabezado[col + 1]) === "FINAL") {
          // La fecha quedo en la columna INICIAL; la FINAL real es la de al lado.
          celdasFinal.push({ valor: celda.valor, columna: col + 2 });
        }
      });
      if (celdasFinal.length < 5) continue;

      const ocupadas = new Set(celdasFinal.map((item) => item.columna - 1));
      const clave = localizarColumnaExacta(filaEncabezado, SINONIMOS_COLUMNA.clave, ocupadas);
      const nombre = localizarColumnaExacta(filaEncabezado, SINONIMOS_COLUMNA.nombre, ocupadas);
      if (clave === -1 || nombre === -1) continue;

      return { filaEncabezado: indiceFilaEncabezado + 1, filaFechas: candidata.fila, celdasFinal };
    }
  }
  return null;
}

// Comun a los dos modos (SEMANAL/CALENDARIO)
function localizarColumnasBase(filaEncabezado, columnasOcupadas) {
  const resolver = (tipo) => {
    const indice = localizarColumnaExacta(filaEncabezado, SINONIMOS_COLUMNA[tipo], columnasOcupadas);
    if (indice !== -1) columnasOcupadas.add(indice);
    return indice;
  };
  return {
    lote: resolver("lote"),
    clave: resolver("clave"),
    nombre: resolver("nombre"),
    presentacion: resolver("presentacion"),
  };
}

const MENSAJE_SIN_FORMATO =
  "No se reconoce el formato de este archivo. Descarga la plantilla, llenala y vuelve a subirla.";

// Modo SEMANAL: arma una partida por producto con `cantidades` = { diaSemana: cantidad }
function extraerSemanal(hoja, encabezadoDias) {
  const indiceEncabezado = encabezadoDias.fila - 1;
  const filaEncabezado = hoja.grid[indiceEncabezado] || [];
  const dias = encabezadoDias.celdas
    .map((celda) => ({ dia: celda.dia, nombreDia: celda.nombreDia, indice: celda.columna - 1 }))
    .sort((a, b) => a.indice - b.indice);

  const ocupadas = new Set(dias.map((item) => item.indice));
  const columnas = { ...localizarColumnasBase(filaEncabezado, ocupadas) };
  const indiceMensual = localizarColumnaExacta(filaEncabezado, SINONIMOS_COLUMNA.mensual, ocupadas);
  if (indiceMensual !== -1) ocupadas.add(indiceMensual);
  columnas.mensual = indiceMensual;
  columnas.dias = dias;

  const partidas = [];
  let sinCantidad = 0;

  for (let i = indiceEncabezado + 1; i < hoja.grid.length; i += 1) {
    const fila = hoja.grid[i];
    if (!Array.isArray(fila)) continue;

    const clave = String(fila[columnas.clave] ?? "").trim();
    const nombre = String(fila[columnas.nombre] ?? "").trim();
    if (!clave && !nombre) continue;

    // La hoja puede repetir el encabezado si trae mas de una semana
    const esOtroEncabezado = dias.some(
      (dia) => normalizar(fila[dia.indice]) === normalizar(filaEncabezado[dia.indice])
    );
    if (esOtroEncabezado) continue;

    const cantidades = {};
    let tieneCantidad = false;
    dias.forEach((dia) => {
      const cantidad = parseNumero(fila[dia.indice]);
      if (cantidad > 0) {
        cantidades[dia.dia] = cantidad;
        tieneCantidad = true;
      }
    });

    const cantidadMensual = columnas.mensual === -1 ? 0 : parseNumero(fila[columnas.mensual]);
    if (cantidadMensual > 0) tieneCantidad = true;

    if (!tieneCantidad) {
      sinCantidad += 1;
      continue;
    }

    partidas.push({
      fila: i + 1,
      clave,
      nombre,
      presentacion: columnas.presentacion === -1 ? "" : String(fila[columnas.presentacion] ?? "").trim(),
      lote: columnas.lote === -1 ? "" : String(fila[columnas.lote] ?? "").trim(),
      cantidades,
      cantidadMensual,
      cantidadesPorFecha: [],
    });
  }

  return { ok: true, hoja: hoja.nombre, filaEncabezado: encabezadoDias.fila, columnas, partidas, sinCantidad };
}

function extraerCalendario(hoja, encabezadoFechas) {
  const indiceEncabezado = encabezadoFechas.fila - 1;
  const filaEncabezado = hoja.grid[indiceEncabezado] || [];
  const fechas = encabezadoFechas.celdas
    .map((celda) => ({ fecha: celda.valor, indice: celda.columna - 1 }))
    .sort((a, b) => a.indice - b.indice);

  const ocupadas = new Set(fechas.map((item) => item.indice));
  const columnas = { ...localizarColumnasBase(filaEncabezado, ocupadas), fechas };

  const partidas = [];
  let sinCantidad = 0;

  for (let i = indiceEncabezado + 1; i < hoja.grid.length; i += 1) {
    const fila = hoja.grid[i];
    if (!Array.isArray(fila)) continue;

    const clave = String(fila[columnas.clave] ?? "").trim();
    const nombre = String(fila[columnas.nombre] ?? "").trim();
    if (!clave && !nombre) continue;

    const cantidadesPorFecha = [];
    let tieneCantidad = false;
    fechas.forEach((item) => {
      const cantidad = parseNumero(fila[item.indice]);
      if (cantidad > 0) {
        cantidadesPorFecha.push({ fecha: item.fecha, cantidad });
        tieneCantidad = true;
      }
    });

    if (!tieneCantidad) {
      sinCantidad += 1;
      continue;
    }

    partidas.push({
      fila: i + 1,
      clave,
      nombre,
      presentacion: columnas.presentacion === -1 ? "" : String(fila[columnas.presentacion] ?? "").trim(),
      lote: columnas.lote === -1 ? "" : String(fila[columnas.lote] ?? "").trim(),
      cantidades: {},
      cantidadMensual: 0,
      cantidadesPorFecha,
    });
  }

  return { ok: true, hoja: hoja.nombre, filaEncabezado: encabezadoFechas.fila, columnas, partidas, sinCantidad };
}

// CALENDARIO_FINAL: como extraerCalendario, pero el encabezado de columnas esta
// una fila abajo de la fila de fechas, y de cada par INICIAL/FINAL solo se usa
// el valor FINAL (la columna INICIAL nunca se lee, ni se suma).
function extraerCalendarioFinal(hoja, bloque) {
  const indiceFilaEncabezado = bloque.filaEncabezado - 1;
  const filaEncabezado = hoja.grid[indiceFilaEncabezado] || [];
  const fechas = bloque.celdasFinal
    .map((celda) => ({ fecha: celda.valor, indice: celda.columna - 1 }))
    .sort((a, b) => a.indice - b.indice);

  const ocupadas = new Set(fechas.map((item) => item.indice));
  const columnas = { ...localizarColumnasBase(filaEncabezado, ocupadas), fechas };

  // Los datos arrancan despues de la fila que quede mas abajo entre encabezado
  // y fechas (el orden entre ambas varia segun el archivo).
  const indiceInicioDatos = Math.max(bloque.filaEncabezado, bloque.filaFechas);
  const partidas = [];
  let sinCantidad = 0;

  for (let i = indiceInicioDatos; i < hoja.grid.length; i += 1) {
    const fila = hoja.grid[i];
    if (!Array.isArray(fila)) continue;

    const clave = String(fila[columnas.clave] ?? "").trim();
    const nombre = String(fila[columnas.nombre] ?? "").trim();
    if (!clave && !nombre) continue;

    const cantidadesPorFecha = [];
    let tieneCantidad = false;
    fechas.forEach((item) => {
      // item.indice es la columna FINAL; la columna INICIAL (item.indice - 1) nunca se lee
      const cantidad = parseNumero(fila[item.indice]);
      if (cantidad > 0) {
        cantidadesPorFecha.push({ fecha: item.fecha, cantidad });
        tieneCantidad = true;
      }
    });

    if (!tieneCantidad) {
      sinCantidad += 1;
      continue;
    }

    partidas.push({
      fila: i + 1,
      clave,
      nombre,
      presentacion: columnas.presentacion === -1 ? "" : String(fila[columnas.presentacion] ?? "").trim(),
      lote: columnas.lote === -1 ? "" : String(fila[columnas.lote] ?? "").trim(),
      cantidades: {},
      cantidadMensual: 0,
      cantidadesPorFecha,
    });
  }

  return { ok: true, hoja: hoja.nombre, filaEncabezado: bloque.filaEncabezado, columnas, partidas, sinCantidad };
}

// Prueba SEMANAL, luego CALENDARIO (una fila), luego CALENDARIO_FINAL (dos filas)
export function extraerPedidoIMSS(hoja) {
  const filaDias = localizarFilaValida(candidatasDeDias(hoja), hoja);
  if (filaDias) return extraerSemanal(hoja, filaDias);

  const candidatasFechas = candidatasDeFechas(hoja);

  const filaFechas = localizarFilaValida(candidatasFechas, hoja);
  if (filaFechas) return extraerCalendario(hoja, filaFechas);

  const bloqueCalendarioFinal = localizarBloqueCalendarioFinal(candidatasFechas, hoja);
  if (bloqueCalendarioFinal) return extraerCalendarioFinal(hoja, bloqueCalendarioFinal);

  return { ok: false, motivo: MENSAJE_SIN_FORMATO };
}

// El patron de dos filas (CALENDARIO_FINAL) se prueba en TODAS las hojas antes
// que los de una sola fila: asi, si el libro trae ademas una hoja auxiliar ya
// aplanada a una columna por dia (ej. "Hoja1"), no le gana el paso a la hoja
// real con INICIAL/FINAL solo por venir antes en el libro.
// Si ninguna hoja trae una tabla reconocida, se queda con la primera
export function elegirHojaMatriz(analisis) {
  const indiceCalendarioFinal = analisis.hojas.findIndex((hoja) =>
    Boolean(localizarBloqueCalendarioFinal(candidatasDeFechas(hoja), hoja))
  );
  if (indiceCalendarioFinal !== -1) return indiceCalendarioFinal;

  const indice = analisis.hojas.findIndex(
    (hoja) => localizarFilaValida(candidatasDeDias(hoja), hoja) || localizarFilaValida(candidatasDeFechas(hoja), hoja)
  );
  return indice === -1 ? 0 : indice;
}

export function volcarEnConsola(analisis, extraccion) {
  /* eslint-disable no-console */
  console.log("===== IMPORTAR PEDIDOS - PARSEO DEL EXCEL =====");
  console.log(
    JSON.stringify(
      {
        archivo: analisis.nombreArchivo,
        totalHojas: analisis.totalHojas,
        hojas: analisis.nombresHojas,
      },
      null,
      2
    )
  );

  analisis.hojas.forEach((hoja) => {
    console.log(`----- HOJA "${hoja.nombre}" : RESUMEN -----`);
    console.log(
      JSON.stringify(
        {
          nombre: hoja.nombre,
          ref: hoja.ref,
          totalFilas: hoja.totalFilas,
          filasNoVacias: hoja.filasNoVacias,
          totalColumnas: hoja.totalColumnas,
          merges: hoja.merges.length,
          diasSemanaEncontrados: hoja.pistas.diasSemana,
          fechasEncontradas: hoja.pistas.fechas,
        },
        null,
        2
      )
    );
  });

  /*console.log(
    JSON.stringify(
      extraccion?.ok
        ? {
            ok: true,
            hoja: extraccion.hoja,
            filaEncabezado: extraccion.filaEncabezado,
            columnas: extraccion.columnas,
            totalPartidas: extraccion.partidas.length,
            filasSinCantidad: extraccion.sinCantidad,
            primerasPartidas: extraccion.partidas.slice(0, 10),
          }
        : extraccion,
      null,
      2
    )
  );*/

  // Solo cuando no se pudo extraer hace falta ver la hoja completa
  if (!extraccion?.ok) {
    analisis.hojas.forEach((hoja) => {
      console.log(`----- HOJA "${hoja.nombre}" : REJILLA CRUDA -----`);
      console.log(JSON.stringify(hoja.grid, null, 2));
    });
  }

  console.log("===== FIN. Objeto completo en window.__pedidosIMSS =====");
  /* eslint-enable no-console */

  if (typeof window !== "undefined") {
    window.__pedidosIMSS = analisis;
  }
}

export { MXN, normalizar, DIAS_SEMANA };
